// API-Client für den V3 Hono-Worker.
//
// Base-URL-Strategie:
// - Tests/Node: `''` (relative URLs gegen das gemockte `fetch`)
// - Dev: `/api` (Vite-Proxy → http://localhost:8787)
// - Prod: `import.meta.env.VITE_API_BASE_URL` (z.B. https://eve-api-v2.drichter77.workers.dev)
//
// V1-Pendant: `WORKER_URL` aus `useAuth.js` + `sbFetch.js`. V3 konsolidiert das
// in einen einzigen Client da V3 keinen Split-Brain-Worker mehr hat.

/**
 * Liefert die API-Base-URL. Default ist relativer `/api`-Path (Vite-Proxy).
 * In Tests gibt's keinen Vite-Proxy — Tests müssen Base-URL explizit setzen
 * oder fetch komplett mocken.
 */
export function getApiBase(): string {
  // Vitest-Umgebung: import.meta.env wird durch Vite injiziert, im jsdom-Test
  // ist es leer. Wir nehmen den expliziten env-Wert oder fallen auf /api zurück.
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) {
      return import.meta.env.VITE_API_BASE_URL;
    }
  } catch {
    // Some test runners shim import.meta; fall through to default.
  }
  return '/api';
}

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON-Body (wird automatisch stringified) */
  json?: unknown;
  /** Access-Token für Bearer-Auth */
  accessToken?: string | null;
  /** Erlaubt absolute URLs zu überschreiben (sonst wird Base-URL prepended) */
  baseUrl?: string;
}

/**
 * Standard-API-Fehler. `cause` enthält den Original-Response-Status.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Ruft den V3-Worker mit JSON-Body + optionalem Bearer auf. Wirft `ApiError`
 * bei non-2xx. Parst Response immer als JSON (Worker garantiert das).
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { json, accessToken, baseUrl, headers, ...rest } = options;
  const base = baseUrl ?? getApiBase();
  const url = path.startsWith('http') ? path : `${base}${path}`;

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(json !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  const res = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : undefined,
  });

  let body: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const message =
      (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string'
        ? body.error
        : `HTTP ${res.status} ${res.statusText}`) || 'API-Fehler';
    throw new ApiError(message, res.status, body);
  }

  return body as T;
}
